import React, { useCallback, useEffect, useState, createElement } from 'react';
import { RotateCcwIcon, SearchIcon, CalendarIcon, EyeIcon, PrinterIcon, PackageIcon, BuildingIcon, UserIcon, BanIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { supabaseAdmin } from '../lib/supabase';
import { Visit } from '../types';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import jsPDF from 'jspdf';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png';
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
async function printReciboDevolucion(visit: Visit, codigoCliente: string) {
  const pw = 80;
  const ml = 4;
  const mr = 4;
  const cw = pw - ml - mr;
  const cx = pw / 2;
  const items = visit.devolucionItems || [];
  const totalUnidades = items.reduce((sum, item) => sum + item.cantidad, 0);
  // Calculate dynamic height based on items
  const baseHeight = 160;
  const rowHeight = 18; // each product takes ~18mm (name + sku + details)
  const ph = Math.max(baseHeight + items.length * rowHeight, 200);
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [pw, ph]
  });
  const fmtDate = (d: Date) => new Date(d).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
  const fmtDateLong = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  const dashedLine = (y: number) => {
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(ml, y, pw - mr, y);
    doc.setLineDashPattern([], 0);
  };
  let y = 2;
  // ── LOGO + HEADER ─────────────────────────────────────────────────────────
  const logoBase64 = await getLogoBase64();
  if (logoBase64) {
    const logoSize = 14;
    doc.addImage(logoBase64, 'PNG', cx - logoSize / 2, y, logoSize, logoSize);
    y += logoSize + 2;
  }
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('MILADYS RODRIGUEZ & ASO.', cx, y, {
    align: 'center'
  });
  y += 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Av. Antonio Guzmán No.102 • 809-247-1006', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('Santiago, Rep. Dom.', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('RNC: 130-329291', cx, y, {
    align: 'center'
  });
  y += 5;
  dashedLine(y);
  y += 5;
  // ── TITLE ─────────────────────────────────────────────────────────────────
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(180, 30, 30);
  doc.text('RECIBO DE DEVOLUCIÓN', cx, y, {
    align: 'center'
  });
  doc.setTextColor(0, 0, 0);
  y += 6;
  // ── RECEIPT NUMBER & DATE ─────────────────────────────────────────────────
  const receiptNum = 'DEV-' + visit.id.replace('temp-', '').replace(/-/g, '').slice(-4).toUpperCase();
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('No: ' + receiptNum, ml, y);
  doc.setFont('helvetica', 'normal');
  doc.text('Fecha: ' + fmtDate(visit.fecha), pw - mr, y, {
    align: 'right'
  });
  y += 5;
  dashedLine(y);
  y += 5;
  // ── CLIENT & VENDEDOR ─────────────────────────────────────────────────────
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Cliente:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(visit.clienteNombre, ml + 14, y);
  y += 5;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Código:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(codigoCliente || '-', ml + 14, y);
  y += 5;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Zona:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(visit.zona || '-', ml + 14, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('Vendedor:', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.text(visit.vendedorNombre, ml + 18, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Motivo: Productos vencidos / devolución', ml, y);
  y += 5;
  dashedLine(y);
  y += 4;
  // ── PRODUCTS TABLE ────────────────────────────────────────────────────────
  // Header — white background with bold black text for thermal printer compatibility
  const tRowH = 5.5;
  doc.setFillColor(255, 255, 255);
  doc.rect(ml, y, cw, tRowH, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(ml, y, cw, tRowH, 'S');
  doc.setLineWidth(0.5);
  doc.line(ml, y + tRowH, ml + cw, y + tRowH);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  // Columns: PRODUCTO (30) | CANT (10) | LOTE (16) | VENCE (16) = 72
  const colWidths = [30, 10, 16, 16];
  const colHeaders = ['PRODUCTO', 'CANT', 'LOTE', 'VENCE'];
  let tx = ml;
  colHeaders.forEach((col, i) => {
    doc.text(col, tx + colWidths[i] / 2, y + tRowH - 1.2, {
      align: 'center'
    });
    tx += colWidths[i];
  });
  y += tRowH;
  // Data rows
  doc.setTextColor(0, 0, 0);
  items.forEach((item, idx) => {
    const isEven = idx % 2 === 0;
    const nameRowH = 5;
    const skuRowH = 3.5;
    const totalItemH = nameRowH + skuRowH + 1;
    // Background
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
    doc.rect(ml, y, cw, totalItemH, 'F');
    doc.setDrawColor(220, 225, 230);
    doc.setLineWidth(0.1);
    doc.line(ml, y + totalItemH, pw - mr, y + totalItemH);
    // Product name (truncated to fit)
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    const maxNameWidth = colWidths[0] - 2;
    let displayName = item.productoNombre;
    while (doc.getTextWidth(displayName) > maxNameWidth && displayName.length > 3) {
      displayName = displayName.slice(0, -1);
    }
    if (displayName !== item.productoNombre) displayName += '..';
    doc.text(displayName, ml + 1, y + nameRowH - 0.5);
    // SKU below name
    doc.setFontSize(5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(item.productoSku, ml + 1, y + nameRowH + skuRowH - 1);
    doc.setTextColor(0, 0, 0);
    // Cantidad - centered, bold
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(String(item.cantidad), ml + colWidths[0] + colWidths[1] / 2, y + totalItemH / 2 + 1.5, {
      align: 'center'
    });
    // Lote
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text(item.lote || '-', ml + colWidths[0] + colWidths[1] + colWidths[2] / 2, y + totalItemH / 2 + 1.5, {
      align: 'center'
    });
    // Fecha vencimiento
    doc.text(item.fechaVencimiento ? fmtDateLong(item.fechaVencimiento) : '-', ml + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] / 2, y + totalItemH / 2 + 1.5, {
      align: 'center'
    });
    y += totalItemH;
  });
  // ── TOTAL ROW ─────────────────────────────────────────────────────────────
  const totalRowH = 7;
  doc.setFillColor(240, 245, 255);
  doc.rect(ml, y, cw, totalRowH, 'F');
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.3);
  doc.rect(ml, y, cw, totalRowH, 'S');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('TOTAL UNIDADES:', ml + 2, y + totalRowH - 2);
  doc.setFontSize(10);
  doc.text(String(totalUnidades), pw - mr - 2, y + totalRowH - 1.5, {
    align: 'right'
  });
  doc.setTextColor(0, 0, 0);
  y += totalRowH + 4;
  dashedLine(y);
  y += 7;
  // ── SIGNATURE LINES ───────────────────────────────────────────────────────
  const sigW = 28;
  doc.setLineWidth(0.3);
  doc.line(ml, y, ml + sigW, y);
  doc.line(pw - mr - sigW, y, pw - mr, y);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Entregado (Cliente)', ml + sigW / 2, y + 4, {
    align: 'center'
  });
  doc.text('Recibido (Vendedor)', pw - mr - sigW / 2, y + 4, {
    align: 'center'
  });
  y += 10;
  dashedLine(y);
  y += 6;
  // ── FOOTER ────────────────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('--- Devolución Registrada ---', cx, y, {
    align: 'center'
  });
  y += 7;
  // Barcode simulation
  const barY = y;
  const barH = 10;
  let bx = ml;
  const seed = receiptNum.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  for (let i = 0; i < 60; i++) {
    const w = seed * (i + 7) * 13 % 3 * 0.4 + 0.5;
    if (i % 2 === 0) {
      doc.setFillColor(0, 0, 0);
      doc.rect(bx, barY, w, barH, 'F');
    }
    bx += w + 0.3;
    if (bx > ml + cw - 2) break;
  }
  y += barH + 4;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RECIBO: ' + receiptNum, cx, y, {
    align: 'center'
  });
  window.open(String(doc.output('bloburl')), '_blank');
}
export function DevolucionesPage() {
  const {
    clients
  } = useApp();
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [anularVisit, setAnularVisit] = useState<Visit | null>(null);
  const [isAnulando, setIsAnulando] = useState(false);
  // ── Fetch visits with devoluciones directly from DB ─────────────────────
  // The global visits from AppContext no longer include devolucionItems (egress optimization).
  // This page fetches ONLY visits that have devoluciones — a small, targeted query.
  const [devVisits, setDevVisits] = useState<Visit[]>([]);
  const [isLoadingDev, setIsLoadingDev] = useState(true);
  const fetchDevoluciones = useCallback(async () => {
    setIsLoadingDev(true);
    try {
      const {
        data
      } = await supabaseAdmin.from('visits').select('id,vendedor_id,vendedor_nombre,cliente_id,cliente_nombre,zona,tipo_visita,fecha,hora_llegada,hora_salida,tiempo_total,estado,numero_recibo,devolucion_items,devolucion_anulada,productos_vencidos').not('devolucion_items', 'eq', '[]').order('created_at', {
        ascending: false
      }).limit(200);
      if (data) {
        const mapped: Visit[] = data.filter((row: any) => row.devolucion_items && row.devolucion_items.length > 0).map((row: any): Visit => ({
          id: row.id,
          vendedorId: row.vendedor_id,
          vendedorNombre: row.vendedor_nombre,
          clienteId: row.cliente_id,
          clienteNombre: row.cliente_nombre,
          zona: row.zona,
          tipoVisita: row.tipo_visita,
          fecha: new Date(row.fecha),
          horaLlegada: row.hora_llegada ? new Date(row.hora_llegada) : null,
          horaSalida: row.hora_salida ? new Date(row.hora_salida) : null,
          tiempoTotal: row.tiempo_total,
          ubicacionLlegada: null,
          ubicacionSalida: null,
          cobro: {
            huboCobo: false,
            montoCobrado: 0,
            metodoPago: null,
            numeroFactura: '',
            estadoFactura: 'no_pago',
            credito: 0,
            descuento: 0
          },
          venta: {
            huboVenta: false,
            montoVenta: 0,
            numeroPedido: ''
          },
          comentarios: '',
          motivoNoPago: '',
          productosVencidos: row.productos_vencidos ?? null,
          devolucionItems: row.devolucion_items || [],
          proximaVisita: null,
          estado: row.estado,
          numeroRecibo: row.numero_recibo || null,
          devolucionAnulada: row.devolucion_anulada || false
        }) as any);
        setDevVisits(mapped);
      }
    } catch (err) {
      console.error('Error loading devoluciones:', err);
    } finally {
      setIsLoadingDev(false);
    }
  }, []);
  useEffect(() => {
    fetchDevoluciones();
  }, [fetchDevoluciones]);
  const [anulados, setAnulados] = useState<Set<string>>(() => {
    const set = new Set<string>();
    devVisits.forEach(v => {
      if ((v as any).devolucionAnulada) set.add(v.id);
    });
    return set;
  });
  // Update anulados when devVisits loads
  useEffect(() => {
    const set = new Set<string>();
    devVisits.forEach(v => {
      if ((v as any).devolucionAnulada) set.add(v.id);
    });
    setAnulados(set);
  }, [devVisits]);
  const handleAnularDevolucion = async () => {
    if (!anularVisit) return;
    setIsAnulando(true);
    try {
      await supabaseAdmin.from('visits').update({
        devolucion_anulada: true
      }).eq('id', anularVisit.id);
      setAnulados(prev => new Set([...prev, anularVisit.id]));
      addActivityLog('Anuló devolución', `Cliente: ${anularVisit.clienteNombre} | Productos: ${anularVisit.devolucionItems?.length || 0}`);
      setAnularVisit(null);
    } catch (err) {
      console.error('Error anulando devolución:', err);
    } finally {
      setIsAnulando(false);
    }
  };
  const ITEMS_PER_PAGE = 10;
  // Filter visits that have devoluciones — now using local devVisits
  const devolucionesVisits = devVisits.filter(v => v.devolucionItems && v.devolucionItems.length > 0);
  // Apply search and role filters
  const filteredVisits = devolucionesVisits.filter(v => isAdmin || v.vendedorId === user?.id).filter(v => {
    if (dateFilter) {
      const visitDate = new Date(v.fecha).toISOString().split('T')[0];
      if (visitDate !== dateFilter) return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return v.clienteNombre.toLowerCase().includes(query) || v.vendedorNombre.toLowerCase().includes(query) || v.devolucionItems?.some(item => item.productoNombre.toLowerCase().includes(query) || item.productoSku.toLowerCase().includes(query));
    }
    return true;
  });
  // Sorting setup
  const {
    items: sortedVisits,
    requestSort,
    sortConfig
  } = useSortableData(filteredVisits, {
    fecha: v => new Date(v.fecha).getTime(),
    cliente: v => v.clienteNombre,
    vendedor: v => v.vendedorNombre,
    cantidad: v => v.devolucionItems?.reduce((sum, item) => sum + item.cantidad, 0) || 0,
    valor: v => v.devolucionItems?.reduce((sum, item) => sum + item.cantidad * ((item as any).precioUnitario || 0), 0) || 0
  });
  // Default sort by date descending if no sort config
  const finalVisits = sortConfig ? sortedVisits : [...sortedVisits].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalPages = Math.ceil(finalVisits.length / ITEMS_PER_PAGE);
  const paginatedVisits = finalVisits.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const formatTime = (date: Date | null) => date ? new Date(date).toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit'
  }) : '--:--';
  const handlePrintRecibo = async (visit: Visit) => {
    setPrintingId(visit.id);
    try {
      const client = clients.find(c => c.id === visit.clienteId);
      const codigoCliente = client?.codigo || client?.id?.slice(0, 8) || '-';
      await printReciboDevolucion(visit, codigoCliente);
    } finally {
      setPrintingId(null);
    }
  };
  return <div className="space-y-6" data-id="element-2043">
      <PageHeader title="Devoluciones" subtitle="Registro de productos devueltos o vencidos" icon={<RotateCcwIcon className="w-6 h-6" data-id="element-2045" />} data-id="element-2044">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto" data-id="element-2046">
          <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-2048" />} data-id="element-2047" />
          <div className="relative w-full sm:w-64" data-id="element-2049">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2050" />
            <input type="text" placeholder="Buscar cliente o producto..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" data-id="element-2051" />
          </div>
        </div>
      </PageHeader>

      {/* Table */}
      <Card padding="none" className="animate-stagger-1" data-id="element-2052">
        {filteredVisits.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-2053">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-50 rounded-2xl mb-4" data-id="element-2054">
              <RotateCcwIcon className="w-8 h-8 text-rose-400" data-id="element-2055" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-2056">
              No hay devoluciones
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-2057">
              {dateFilter || searchQuery ? 'No se encontraron devoluciones con los filtros aplicados' : 'Aún no hay devoluciones registradas en el sistema'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-2058">
            <table className="w-full" data-id="element-2059">
              <thead data-id="element-2060">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-2061">
                  <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2062" />
                  <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2063" />
                  {isAdmin && <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2064" />}
                  <SortableTableHeader label="Total Productos" sortKey="cantidad" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2065" />
                  <SortableTableHeader label="Valor" sortKey="valor" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2066" />
                  <th className="px-5 py-4 w-24" data-id="element-2067"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2068">
                {paginatedVisits.map(visit => {
              const totalItems = visit.devolucionItems?.reduce((sum, item) => sum + item.cantidad, 0) || 0;
              const uniqueProducts = visit.devolucionItems?.length || 0;
              const isPrinting = printingId === visit.id;
              return <tr key={visit.id} className="hover:bg-rose-50/30 transition-colors cursor-pointer" onClick={() => setSelectedVisit(visit)} data-id="element-2069">
                      <td className="px-5 py-4" data-id="element-2070">
                        <p className="font-medium text-slate-900 text-sm" data-id="element-2071">
                          {formatDate(visit.fecha)}
                        </p>
                        <p className="text-xs text-slate-400" data-id="element-2072">
                          {formatTime(visit.horaLlegada)}
                        </p>
                      </td>
                      <td className="px-5 py-4" data-id="element-2073">
                        <p className="font-medium text-slate-900 text-sm" data-id="element-2074">
                          {visit.clienteNombre}
                        </p>
                        <p className="text-xs text-slate-400" data-id="element-2075">{visit.zona}</p>
                      </td>
                      {isAdmin && <td className="px-5 py-4 text-sm text-slate-700" data-id="element-2076">
                          {visit.vendedorNombre}
                        </td>}
                      <td className="px-5 py-4 text-center" data-id="element-2077">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-rose-50 text-rose-700" data-id="element-2078">
                          <PackageIcon className="w-3.5 h-3.5" data-id="element-2079" />
                          <span className="font-bold text-sm" data-id="element-2080">
                            {totalItems}
                          </span>
                          <span className="text-xs opacity-80" data-id="element-2081">
                            ({uniqueProducts} ref)
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right" data-id="element-2082">
                        {(() => {
                    const totalVal = visit.devolucionItems?.reduce((sum, item) => sum + item.cantidad * ((item as any).precioUnitario || 0), 0) || 0;
                    return totalVal > 0 ? <span className="font-bold text-rose-700 text-sm" data-id="element-2083">
                              RD$
                              {totalVal.toLocaleString('es-DO', {
                        minimumFractionDigits: 2
                      })}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-2084">—</span>;
                  })()}
                      </td>
                      <td className="px-5 py-4" data-id="element-2085">
                        <div className="flex items-center gap-1" data-id="element-2086">
                          <Button variant="ghost" size="sm" onClick={e => {
                      e.stopPropagation();
                      setSelectedVisit(visit);
                    }} data-id="element-2087">
                            <EyeIcon className="w-4 h-4 text-slate-400" data-id="element-2088" />
                          </Button>
                          {!anulados.has(visit.id) && <>
                              <button onClick={e => {
                        e.stopPropagation();
                        handlePrintRecibo(visit);
                      }} disabled={isPrinting} title="Imprimir recibo" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors disabled:opacity-40" data-id="element-2089">
                                {isPrinting ? <div className="w-4 h-4 border-2 border-rose-200 border-t-rose-600 rounded-full animate-spin" data-id="element-2090" /> : <PrinterIcon className="w-4 h-4" data-id="element-2091" />}
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setAnularVisit(visit);
                      }} title="Anular recibo" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-2092">
                                <BanIcon className="w-4 h-4" data-id="element-2093" />
                              </button>
                            </>}
                          {anulados.has(visit.id) && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500" data-id="element-2094">
                              <BanIcon className="w-3 h-3" data-id="element-2095" /> Anulado
                            </span>}
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </Card>

      <div className="animate-stagger-2" data-id="element-2096">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredVisits.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-2097" />
      </div>

      {/* Detail Modal */}
      <Modal isOpen={!!selectedVisit} onClose={() => setSelectedVisit(null)} title="Detalle de Devolución" size="lg" data-id="element-2098">
        {selectedVisit && <>
            <ModalBody data-id="element-2099">
              <div className="space-y-6" data-id="element-2100">
                {/* Header Info */}
                <div className="grid grid-cols-2 gap-4" data-id="element-2101">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-2102">
                    <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0" data-id="element-2103">
                      <BuildingIcon className="w-5 h-5 text-indigo-600" data-id="element-2104" />
                    </div>
                    <div data-id="element-2105">
                      <p className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-2106">
                        Cliente
                      </p>
                      <p className="font-bold text-slate-900 text-sm" data-id="element-2107">
                        {selectedVisit.clienteNombre}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-2108">
                        {selectedVisit.zona}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-2109">
                    <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0" data-id="element-2110">
                      <UserIcon className="w-5 h-5 text-emerald-600" data-id="element-2111" />
                    </div>
                    <div data-id="element-2112">
                      <p className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-2113">
                        Vendedor
                      </p>
                      <p className="font-bold text-slate-900 text-sm" data-id="element-2114">
                        {selectedVisit.vendedorNombre}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-2115">
                        {formatDate(selectedVisit.fecha)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Products List */}
                <div data-id="element-2116">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-2117">
                    <PackageIcon className="w-5 h-5 text-rose-500" data-id="element-2118" />
                    Productos Devueltos
                  </h4>
                  <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-2119">
                    <table className="w-full text-sm" data-id="element-2120">
                      <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2121">
                        <tr data-id="element-2122">
                          <th className="px-4 py-3 text-left font-semibold text-slate-600" data-id="element-2123">
                            Producto
                          </th>
                          <th className="px-4 py-3 text-center font-semibold text-slate-600" data-id="element-2124">
                            Cantidad
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-600" data-id="element-2125">
                            Precio Unit.
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-600" data-id="element-2126">
                            Valor
                          </th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-600" data-id="element-2127">
                            Lote
                          </th>
                          <th className="px-4 py-3 text-left font-semibold text-slate-600" data-id="element-2128">
                            Vencimiento
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-2129">
                        {selectedVisit.devolucionItems?.map((item, idx) => {
                      const precio = (item as any).precioUnitario || 0;
                      const valor = item.cantidad * precio;
                      return <tr key={idx} className="bg-white" data-id="element-2130">
                              <td className="px-4 py-3" data-id="element-2131">
                                <p className="font-semibold text-slate-900" data-id="element-2132">
                                  {item.productoNombre}
                                </p>
                                <p className="text-xs text-slate-500 font-mono" data-id="element-2133">
                                  {item.productoSku}
                                </p>
                              </td>
                              <td className="px-4 py-3 text-center" data-id="element-2134">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-rose-50 text-rose-700 font-bold" data-id="element-2135">
                                  {item.cantidad}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-slate-600" data-id="element-2136">
                                {precio > 0 ? `RD${precio.toLocaleString('es-DO', {
                            minimumFractionDigits: 2
                          })}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-rose-700" data-id="element-2137">
                                {valor > 0 ? `RD${valor.toLocaleString('es-DO', {
                            minimumFractionDigits: 2
                          })}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-slate-700 font-medium" data-id="element-2138">
                                {item.lote || '-'}
                              </td>
                              <td className="px-4 py-3 text-slate-700" data-id="element-2139">
                                {item.fechaVencimiento ? new Date(item.fechaVencimiento).toLocaleDateString('es-DO', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : '-'}
                              </td>
                            </tr>;
                    })}
                      </tbody>
                      <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-2140">
                        <tr data-id="element-2141">
                          <td className="px-4 py-3 text-right font-bold text-slate-700" data-id="element-2142">
                            Totales:
                          </td>
                          <td className="px-4 py-3 text-center font-black text-rose-600 text-base" data-id="element-2143">
                            {selectedVisit.devolucionItems?.reduce((sum, item) => sum + item.cantidad, 0)}
                          </td>
                          <td className="px-4 py-3" data-id="element-2144"></td>
                          <td className="px-4 py-3 text-right font-black text-rose-700 text-base" data-id="element-2145">
                            RD$
                            {(selectedVisit.devolucionItems?.reduce((sum, item) => sum + item.cantidad * ((item as any).precioUnitario || 0), 0) || 0).toLocaleString('es-DO', {
                          minimumFractionDigits: 2
                        })}
                          </td>
                          <td colSpan={2} data-id="element-2146"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter data-id="element-2147">
              <Button variant="secondary" onClick={() => setSelectedVisit(null)} data-id="element-2148">
                Cerrar
              </Button>
              <Button onClick={() => handlePrintRecibo(selectedVisit)} disabled={printingId === selectedVisit.id} leftIcon={printingId === selectedVisit.id ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" data-id="element-2150" /> : <PrinterIcon className="w-4 h-4" data-id="element-2151" />} className="bg-rose-600 hover:bg-rose-700 text-white" data-id="element-2149">
                Imprimir Recibo
              </Button>
            </ModalFooter>
          </>}
      </Modal>

      {/* Anular Recibo Modal */}
      <Modal isOpen={!!anularVisit} onClose={() => setAnularVisit(null)} title="Anular Recibo de Devolución" size="sm" data-id="element-2152">
        <ModalBody data-id="element-2153">
          <div className="text-center space-y-3" data-id="element-2154">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-rose-50 rounded-2xl" data-id="element-2155">
              <BanIcon className="w-7 h-7 text-rose-500" data-id="element-2156" />
            </div>
            <p className="text-slate-600" data-id="element-2157">
              ¿Estás seguro de anular el recibo de devolución de{' '}
              <strong data-id="element-2158">{anularVisit?.clienteNombre}</strong>?
            </p>
            <p className="text-xs text-slate-400" data-id="element-2159">
              Esta acción marcará el recibo como anulado. No se puede deshacer.
            </p>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2160">
          <Button variant="secondary" onClick={() => setAnularVisit(null)} data-id="element-2161">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleAnularDevolucion} isLoading={isAnulando} leftIcon={<BanIcon className="w-4 h-4" data-id="element-2163" />} data-id="element-2162">
            Anular Recibo
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}